import React, { useEffect } from 'react';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Package, Truck, CheckCircle } from 'lucide-react';
import { BottomSheet, PageLayout } from '@/components';
import { ROUTE } from '@/config';
import { useResendStore } from '@/stores/useResendStore';
import { useBulkResendOrders } from '@/hooksApi/useInventory';
import { useQueryClient } from '@tanstack/react-query';
import ResendStep1 from './ResendStep1';
import ResendStep2 from './ResendStep2';
import ResendStep3 from './ResendStep3';

const stepContents = [
  <ResendStep1 key='step1' />,
  <ResendStep2 key='step2' />,
  <ResendStep3 key='step3' />,
];

const ResendPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const {
    currentStep,
    setCurrentStep,
    selectedOrders,
    orderOverrides,
    carrierSelections,
    reset,
  } = useResendStore();

  const { mutate: bulkResend, isLoading } = useBulkResendOrders();

  const totalSteps = stepContents.length;

  // Guard: if no orders selected (e.g. page refresh), reset and redirect
  useEffect(() => {
    if (selectedOrders.length === 0) {
      reset();
      navigate(`${ROUTE.ORDERS}?activeTab=inventory`, { replace: true });
    }
  }, []);

  const stepperItems = [
    { label: String(t('resend_step1_title')), icon: <Package size={14} /> },
    { label: String(t('resend_step2_title')), icon: <Truck size={14} /> },
    { label: String(t('resend_step3_title')), icon: <CheckCircle size={14} /> },
  ];

  const handleBack = () => {
    if (currentStep === 0) {
      reset();
      navigate(`${ROUTE.ORDERS}?activeTab=inventory`);
    } else {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleNext = () => {
    // On Step 2 (carrier selection), validate that every order has a carrier selected
    if (currentStep === 1) {
      const missingCarrier = selectedOrders.some((order) => {
        const slug = carrierSelections[order.uid] ?? orderOverrides[order.uid]?.carrier_slug ?? order.recommended_carrier_slug;
        return !slug;
      });
      if (missingCarrier) {
        message.warning(String(t(
          selectedOrders.length === 1
            ? 'select_carrier_for_order'
            : 'select_carrier_for_all_orders'
        )));
        return;
      }
    }
    if (currentStep < totalSteps - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleConfirmResend = () => {
    const payload = {
      orders: selectedOrders.map((order) => {
        const override = orderOverrides[order.uid] ?? {};
        const carrierSlug = carrierSelections[order.uid] ?? override.carrier_slug ?? order.recommended_carrier_slug ?? undefined;
        return {
          uid: order.uid,
          customer_data: override.customer_data,
          carrier_slug: carrierSlug,
          // O-22: Include product price overrides if edited
          ...(override.products ? { products: override.products } : {}),
        };
      }),
    };

    bulkResend(payload, {
      onSuccess: () => {
        message.success(String(t('resend_success')));
        queryClient.invalidateQueries(['inventory']);
        navigate(`${ROUTE.ORDERS}?activeTab=inventory`, { replace: true });
        reset();
      },
      onError: () => {
        message.error(String(t('error_title')));
      },
    });
  };

  // Don't render if no orders (guard will redirect)
  if (selectedOrders.length === 0) return null;

  return (
    <PageLayout
      className='min-h-screen'
      bottomSheet={
        <BottomSheet
          sticky
          isVisible
          justify={currentStep === totalSteps - 1 ? 'flex-start' : 'flex-end'}
          wrapperClassName='sd-font !h-auto !max-h-none !p-5 !py-4 !bottom-4 !rounded-xl !border !border-solid !border-[#e8e6e3] !shadow-lg'
        >
          {currentStep === totalSteps - 1 ? (
            <div className='flex w-full items-center justify-between'>
              <div className='flex items-center gap-3'>
                <button
                  type='button'
                  className='h-9 px-4 rounded-lg border border-solid border-[rgb(232,230,227)] bg-white text-[rgb(57,57,60)] text-[13px] font-semibold cursor-pointer hover:bg-[rgb(245,245,243)] transition-colors'
                  onClick={handleBack}
                >
                  {String(t('back'))}
                </button>
                <span className='text-sm text-[rgb(115,123,140)] font-medium'>
                  {selectedOrders.length}{' '}{String(t(selectedOrders.length === 1 ? 'order_selected' : 'orders_selected'))}
                </span>
              </div>
              <button
                type='button'
                className='flex items-center gap-1.5 h-9 px-5 rounded-lg bg-[rgb(237,224,74)] text-[rgb(57,57,60)] text-[13px] font-semibold cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[rgb(230,217,60)] transition-colors'
                onClick={handleConfirmResend}
                disabled={selectedOrders.length === 0 || isLoading}
              >
                {isLoading ? (
                  <span className='h-3.5 w-3.5 rounded-full border-2 border-[rgb(57,57,60)] border-t-transparent animate-spin' />
                ) : (
                  <CheckCircle size={14} />
                )}
                {String(t('confirm_resend'))}
              </button>
            </div>
          ) : (
            <div className='flex w-full justify-between items-center'>
              <div className='flex items-center gap-3'>
                <button
                  type='button'
                  className='h-9 px-4 rounded-lg border border-solid border-[rgb(232,230,227)] bg-white text-[rgb(57,57,60)] text-[13px] font-semibold cursor-pointer hover:bg-[rgb(245,245,243)] transition-colors'
                  onClick={handleBack}
                >
                  {String(t('back'))}
                </button>
                <span className='text-sm text-[rgb(115,123,140)] font-medium'>
                  {selectedOrders.length}{' '}{String(t(selectedOrders.length === 1 ? 'order_selected' : 'orders_selected'))}
                </span>
              </div>
              <button
                type='button'
                className='h-9 px-5 rounded-lg bg-[rgb(237,224,74)] text-[rgb(57,57,60)] text-[13px] font-semibold cursor-pointer hover:bg-[rgb(230,217,60)] transition-colors'
                onClick={handleNext}
              >
                {String(t('continue'))}
              </button>
            </div>
          )}
        </BottomSheet>
      }
    >
      <div className='sd-font mx-auto pb-32 w-full max-w-[900px]'>
        <button
          onClick={() => {
            reset();
            navigate(`${ROUTE.ORDERS}?activeTab=inventory`);
          }}
          className='flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 mb-2 bg-transparent border-none cursor-pointer p-0'
        >
          <ArrowLeft
            size={16}
            style={{ transform: document.dir === 'rtl' ? 'scaleX(-1)' : undefined }}
          />
          <span>{String(t('back_to_orders'))}</span>
        </button>

        <h1 className='text-2xl font-bold text-sd-foreground mt-1'>
          {String(t('resend_order'))}
        </h1>

        <div className='mt-4 mb-6'>
          <div className='w-full h-1.5 rounded-full bg-gray-200 mb-2.5'>
            <div
              className='h-full rounded-full bg-sd-primary transition-all duration-700 ease-out'
              style={{ width: `${(currentStep / (stepperItems.length - 1)) * 100}%` }}
            />
          </div>
          <div className='grid grid-cols-3 w-full'>
            {stepperItems.map((step, index) => (
              <div
                key={index}
                className={`flex items-center gap-1.5 text-xs font-medium ${
                  index <= currentStep ? 'text-sd-foreground' : 'text-gray-500'
                } ${
                  index === 0
                    ? 'justify-self-start'
                    : index === stepperItems.length - 1
                      ? 'justify-self-end'
                      : 'justify-self-center'
                }`}
              >
                {step.icon}
                <span>{step.label}</span>
              </div>
            ))}
          </div>
        </div>

        {stepContents[currentStep]}
      </div>
    </PageLayout>
  );
};

export default ResendPage;
